import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Order, SortParams } from "@/types/types";
import { SignalFilterStatus } from "@/enum/list.enum";

import { initialPageSize } from "@/utils/preferredPageSize";

import { IFilterWithValues } from "../components/FilterModal";

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 15;
const SEARCH_DEBOUNCE_MS = 800;
const DEFAULT_SIGNAL_FILTER = [SignalFilterStatus.ALL];

interface UseTrackerUrlStateOptions {
	prefix: string;
	defaultSortBy: string;
	defaultSortOrder: Order;
}

export const useTrackerUrlState = ({
	prefix,
	defaultSortBy,
	defaultSortOrder,
}: UseTrackerUrlStateOptions) => {
	const [searchParams, setSearchParams] = useSearchParams();

	const paramKey = useCallback((key: string) => `${prefix}_${key}`, [prefix]);

	// ── Read initial values from URL ─────────────────────────────────────
	const initialPage = Number(searchParams.get(paramKey("page"))) || DEFAULT_PAGE;
	const initialSortBy = searchParams.get(paramKey("sortBy")) || defaultSortBy;
	const initialSortOrder = (searchParams.get(paramKey("sortOrder")) as Order) || defaultSortOrder;
	const initialSearch = searchParams.get(paramKey("search")) || "";

	let initialFilter: IFilterWithValues | undefined;

	try {
		const raw = searchParams.get(paramKey("filter"));

		if (raw) {
			initialFilter = JSON.parse(raw);
		}
	} catch {
		initialFilter = undefined;
	}

	let initialSignalFilter: SignalFilterStatus[] = DEFAULT_SIGNAL_FILTER;

	try {
		const raw = searchParams.get(paramKey("signalFilter"));

		if (raw) {
			initialSignalFilter = raw.split(",") as SignalFilterStatus[];
		}
	} catch {
		initialSignalFilter = DEFAULT_SIGNAL_FILTER;
	}

	// ── State ────────────────────────────────────────────────────────────
	const [page, setPageState] = useState(initialPage);
	// A URL limit wins; otherwise the size the user last picked in any table (PREC-4403). The URL
	// omits the default size, so a later page without one reads as the default.
	const [limit, setLimitState] = useState(() =>
		initialPageSize({
			urlLimit: searchParams.get(paramKey("limit")),
			urlPage: searchParams.get(paramKey("page")),
			fallback: DEFAULT_LIMIT,
		})
	);
	const [sorting, setSortingState] = useState<SortParams>({
		sortBy: initialSortBy,
		sortOrder: initialSortOrder,
	});
	const [filter, setFilterState] = useState<IFilterWithValues | undefined>(initialFilter);
	const [signalFilterStatus, setSignalFilterStatusState] =
		useState<SignalFilterStatus[]>(initialSignalFilter);
	const [searchValue, setSearchValue] = useState(initialSearch);
	const [query, setQuery] = useState(initialSearch);
	const [isTyping, setIsTyping] = useState(false);

	// ── Sync state → URL ─────────────────────────────────────────────────
	const syncToUrl = useCallback(
		(updates: Record<string, string | undefined>) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);

					for (const [key, value] of Object.entries(updates)) {
						if (value === undefined || value === "") {
							next.delete(key);
						} else {
							next.set(key, value);
						}
					}

					return next;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	const buildUrlUpdates = useCallback(
		(
			p: number,
			l: number,
			sort: SortParams,
			f: IFilterWithValues | undefined,
			q: string,
			signal: SignalFilterStatus[]
		): Record<string, string | undefined> => {
			const updates: Record<string, string | undefined> = {};

			updates[paramKey("page")] = p !== DEFAULT_PAGE ? String(p) : undefined;
			updates[paramKey("limit")] = l !== DEFAULT_LIMIT ? String(l) : undefined;
			updates[paramKey("sortBy")] = sort.sortBy !== defaultSortBy ? sort.sortBy : undefined;
			updates[paramKey("sortOrder")] =
				sort.sortOrder !== defaultSortOrder ? sort.sortOrder : undefined;
			updates[paramKey("filter")] = f && Object.keys(f).length > 0 ? JSON.stringify(f) : undefined;
			updates[paramKey("search")] = q || undefined;

			const isDefaultSignal = signal.length === 1 && signal[0] === SignalFilterStatus.ALL;

			updates[paramKey("signalFilter")] = !isDefaultSignal ? signal.join(",") : undefined;

			return updates;
		},
		[paramKey, defaultSortBy, defaultSortOrder]
	);

	// ── Setters that sync to URL ─────────────────────────────────────────
	const setPage = useCallback(
		(p: number) => {
			setPageState(p);
			syncToUrl(buildUrlUpdates(p, limit, sorting, filter, query, signalFilterStatus));
		},
		[limit, sorting, filter, query, signalFilterStatus, syncToUrl, buildUrlUpdates]
	);

	const setLimit = useCallback(
		(l: number) => {
			setLimitState(l);
			setPageState(DEFAULT_PAGE);
			syncToUrl(buildUrlUpdates(DEFAULT_PAGE, l, sorting, filter, query, signalFilterStatus));
		},
		[sorting, filter, query, signalFilterStatus, syncToUrl, buildUrlUpdates]
	);

	const setSorting = useCallback(
		(sort: SortParams) => {
			setSortingState(sort);
			setPageState(DEFAULT_PAGE);
			syncToUrl(buildUrlUpdates(DEFAULT_PAGE, limit, sort, filter, query, signalFilterStatus));
		},
		[limit, filter, query, signalFilterStatus, syncToUrl, buildUrlUpdates]
	);

	const setFilter = useCallback(
		(f: IFilterWithValues | undefined) => {
			setFilterState(f);
			setPageState(DEFAULT_PAGE);
			syncToUrl(buildUrlUpdates(DEFAULT_PAGE, limit, sorting, f, query, signalFilterStatus));
		},
		[limit, sorting, query, signalFilterStatus, syncToUrl, buildUrlUpdates]
	);

	const setSignalFilterStatus = useCallback(
		(signal: SignalFilterStatus[]) => {
			setSignalFilterStatusState(signal);
			setPageState(DEFAULT_PAGE);
			syncToUrl(buildUrlUpdates(DEFAULT_PAGE, limit, sorting, filter, query, signal));
		},
		[limit, sorting, filter, query, syncToUrl, buildUrlUpdates]
	);

	// ── Search debounce ──────────────────────────────────────────────────
	const searchTimer = useRef<NodeJS.Timeout | null>(null);
	const isInitialMount = useRef(true);

	useEffect(() => {
		// Skip the first run so we don't reset page on mount / reload
		if (isInitialMount.current) {
			isInitialMount.current = false;

			return;
		}

		setIsTyping(true);

		if (searchTimer.current) {
			clearTimeout(searchTimer.current);
		}

		searchTimer.current = setTimeout(() => {
			setQuery(searchValue);
			setIsTyping(false);
			setPageState(DEFAULT_PAGE);
			syncToUrl(
				buildUrlUpdates(DEFAULT_PAGE, limit, sorting, filter, searchValue, signalFilterStatus)
			);
		}, SEARCH_DEBOUNCE_MS);

		return () => {
			if (searchTimer.current) {
				clearTimeout(searchTimer.current);
			}
		};
	}, [searchValue]);

	return {
		page,
		setPage,
		limit,
		setLimit,
		sorting,
		setSorting,
		filter,
		setFilter,
		signalFilterStatus,
		setSignalFilterStatus,
		searchValue,
		setSearchValue,
		query,
		isTyping,
		searchParams,
		setSearchParams,
	};
};
